import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChatRepository } from 'repository/chatRepository';
import './ChatParticipants.css';

interface Participant {
    userId: string;
    nickname: string;
    profileUrl?: string;
    isOnline: boolean;
    isHost?: boolean;
    lastSeen?: string;
}

export const ChatParticipants = () => {
    const navigate = useNavigate();
    const { roomId } = useParams<{ roomId: string }>();
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [roomName, setRoomName] = useState('채팅방');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!roomId) return;

        const loadData = async () => {
            setIsLoading(true);
            try {
                const [roomRes, participantsRes] = await Promise.all([
                    ChatRepository.getRoomInfo(roomId),
                    ChatRepository.getParticipants(roomId)
                ]);

                if (roomRes?.data) {
                    setRoomName(roomRes.data.roomName);
                }

                if (participantsRes?.data?.list) {
                    setParticipants(participantsRes.data.list);
                }
            } catch (error) {
                console.error('참여자 목록 로드 실패:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [roomId]);

    const onlineCount = participants.filter(p => p.isOnline).length;

    const handleBack = () => {
        navigate(`/chat/room/${roomId}`);
    };

    if (isLoading) {
        return (
            <div className="chat-participants-loading">
                <div className="loading-spinner"></div>
                <p>참여자 목록 로딩 중...</p>
            </div>
        );
    }

    return (
        <div className="chat-participants-wrapper">
            <div className="participants-header">
                <button className="back-button" onClick={handleBack}>
                    ←
                </button>
                <h1>참여자</h1>
            </div>

            <div className="chat-participants-content">
                {/* Room Info */}
                <div className="room-info-card">
                    <div className="room-icon">💬</div>
                    <div className="room-details">
                        <h2 className="room-name">{roomName}</h2>
                        <div className="room-stats">
                            <span className="stat-item">
                                👥 {participants.length}명
                            </span>
                            <span className="stat-separator">•</span>
                            <span className="stat-item online">
                                🟢 {onlineCount}명 접속중
                            </span>
                        </div>
                    </div>
                </div>

                {/* Participants List */}
                <div className="participants-section">
                    <h3 className="section-title">
                        참여자 {participants.length}명
                    </h3>

                    <div className="participants-list">
                        {participants.map(participant => (
                            <div
                                key={participant.userId}
                                className="participant-item"
                            >
                                <div className="participant-avatar-wrapper">
                                    <div className="participant-avatar">
                                        {participant.profileUrl ? (
                                            <img src={participant.profileUrl} alt="" />
                                        ) : (
                                            '👤'
                                        )}
                                    </div>
                                    {participant.isOnline && (
                                        <div className="online-indicator"></div>
                                    )}
                                </div>

                                <div className="participant-info">
                                    <div className="participant-header">
                                        <div className="participant-name">{participant.nickname}</div>
                                        {participant.isHost && (
                                            <div className="host-badge">주최자</div>
                                        )}
                                    </div>
                                    <div className="participant-status">
                                        {participant.isOnline ? (
                                            <span className="status-text online">접속중</span>
                                        ) : (
                                            <span className="status-text offline">
                                                {participant.lastSeen ? `마지막 접속: ${participant.lastSeen}` : '오프라인'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Info Notice */}
                <div className="participants-notice">
                    <div className="notice-icon">💡</div>
                    <div className="notice-text">
                        모임이 종료되면 채팅방이 자동으로 비활성화됩니다
                    </div>
                </div>
            </div>
        </div>
    );
};
